import {
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Portal } from 'components/Portal/Portal';
import { useAnimation } from 'hooks/useAnimation';
import { useHideDocumentScroll } from 'hooks/useHideDocumentScroll';
import { useScrollEffect } from 'hooks/viewport/useScrollEffect';
import { useCurrentModalType } from 'modules/main/store/hooks/useCurrentModalType';
import { ClickOutside } from 'utils/ClickOutside';

import { DefaultTooltipButton } from './DefaultTooltipButton';
import {
  TargetContainer,
  TooltipCloseButton,
  TooltipContainer,
  TooltipOverlay,
  TooltipWrapper,
} from './Tooltip.styled';
import { TooltipProvider } from './TooltipContext';
import { TooltipPosition } from './TooltipPosition';
import { TooltipSize } from './TooltipSize';
import { TooltipVariant } from './TooltipVariant';

type Props = {
  animate?: boolean;
  children?: ReactNode;
  closeable?: boolean;
  content: ReactNode;
  className?: string;
  onClose?: () => void;
  placement?: TooltipPosition;
  tooltipWidth?: number;
  triggerCondition?: boolean;
  size?: TooltipSize;
  variant?: TooltipVariant;
  withArrow?: boolean;
  withOverlay?: boolean;
  onRender?: () => void;
};

export function Tooltip({
  animate = false,
  placement = 'top-center',
  children,
  closeable,
  onClose,
  content,
  className,
  tooltipWidth,
  triggerCondition,
  size = 'small',
  variant = 'brand-blue',
  withArrow = true,
  withOverlay = false,
  onRender,
}: Props) {
  const modalType = useCurrentModalType();

  const [showTooltip, setShowTooltip] = useState(triggerCondition || false);
  const [showOverlay, setShowOverlay] = useState(showTooltip && withOverlay);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const childRef = useRef<HTMLElement>(null);
  const hideTooltipTimeout = useRef<number | null>(null);
  const [targetWidth, setTargetWidth] = useState<number>(0);
  const [targetHeight, setTargetHeight] = useState<number>(0);

  const { stage, shouldRender } = useAnimation(showTooltip, 200);

  const handleShowTooltip = () => {
    if (isClosing) {
      setIsClosing(false);
    }

    if (hideTooltipTimeout.current) {
      clearTimeout(hideTooltipTimeout.current);
    }
    setShowTooltip(true);
  };

  const hideTooltipWithDelay = () => {
    if (!isTooltipHovered) {
      hideTooltipTimeout.current = setTimeout(() => {
        setShowTooltip(false);
      }, 200) as unknown as number; // Without casting, returns NodeJS.Timeout
    }
  };

  const handleTriggerMouseOver = () => handleShowTooltip();
  const handleTriggerMouseLeave = () => hideTooltipWithDelay();

  const handleTooltipMouseOver = () => {
    setIsTooltipHovered(true);
    handleShowTooltip();
  };
  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    if (!triggerCondition) {
      setShowTooltip(false);
      hideTooltipWithDelay();
    }
  };

  const closeTooltip = useCallback(() => {
    setIsClosing(true);
    setShowTooltip(false);
    if (onClose) onClose();
  }, [onClose]);

  useHideDocumentScroll({ show: !showTooltip || !withOverlay });

  useScrollEffect(() => {
    if (withOverlay) return;
    setShowTooltip(false);
  });

  useEffect(
    () => () => {
      if (hideTooltipTimeout.current) {
        clearTimeout(hideTooltipTimeout.current);
      }
    },
    [],
  );
  useEffect(() => {
    setShowTooltip(triggerCondition || false);
    if (withOverlay) {
      setShowOverlay(triggerCondition || false);
    }
  }, [triggerCondition, withOverlay]);

  useEffect(() => {
    if (!targetWidth && !targetHeight && childRef.current) {
      const { width, height } = childRef.current.getBoundingClientRect();

      setTargetWidth(width);
      setTargetHeight(height);
    }
  }, [targetWidth, targetHeight, showTooltip, childRef]);

  useEffect(() => {
    if (shouldRender && onRender) onRender();
  }, [shouldRender, onRender]);

  const childrenToClone = isValidElement(children) ? (
    children
  ) : (
    <DefaultTooltipButton />
  );

  return (
    <TargetContainer>
      {cloneElement(childrenToClone, {
        ref: childRef,
        onMouseOver:
          triggerCondition === undefined ? handleTriggerMouseOver : undefined,
        onMouseLeave: closeable ? undefined : handleTriggerMouseLeave,
      })}
      {shouldRender ? (
        <TooltipProvider value={{ closeTooltip }}>
          <ClickOutside
            onClickOutside={() => {
              if (!withOverlay && !modalType) closeTooltip();
            }}
          >
            <TooltipContainer
              className={className}
              onMouseOver={!isClosing ? handleTooltipMouseOver : undefined}
              onMouseLeave={handleTooltipMouseLeave}
              $animate={animate}
              $targetWidth={targetWidth}
              $targetHeight={targetHeight}
              $placement={placement}
              $variant={variant}
              $size={size}
              $tooltipWidth={tooltipWidth}
              $withArrow={withArrow}
              $stage={stage}
            >
              <TooltipWrapper $size={size}>
                {closeable ? (
                  <TooltipCloseButton
                    onClick={closeTooltip}
                    data-qa-id="close-modal-button"
                    data-modal-focus="false"
                  />
                ) : null}
                {content}
              </TooltipWrapper>
              {showOverlay ? (
                <Portal>
                  <TooltipOverlay />
                </Portal>
              ) : null}
            </TooltipContainer>
          </ClickOutside>
        </TooltipProvider>
      ) : null}
    </TargetContainer>
  );
}
